/**
 * The Line: the composer's live voice session on OpenAI GPT Live 1.
 * Spec: docs/superpowers/specs/2026-09-11-composer-live-line-design.md
 *
 * THE ONE PRICE ROW for voice minutes. OpenAI bills gpt-live-1 sessions at
 * $0.05 per minute, per second, from creation to `session.closed`
 * (developers.openai.com/api/docs/models/gpt-live-1, read 2026-09-11). The
 * server meters `usage.seconds` snapshots against this number through the
 * connector-call ledger (`voice_live`). Change it here and nowhere else.
 */
import { z } from 'zod';
import type { ComposerAttachment, ConverseRequest } from './contract.js';

export const LIVE_VOICE_USD_PER_MINUTE = 0.05;

/** The twelve GPT Live voices plus OpenAI's default, marin. */
export const LIVE_VOICES = [
  'marin',
  'quartz',
  'ripple',
  'vesper',
  'willow',
  'stone',
  'gleam',
  'meridian',
  'beacon',
  'delta',
  'cinder',
  'bossa',
  'tempo',
] as const;
export type LiveVoice = (typeof LIVE_VOICES)[number];
/**
 * Bash is a he, so the default is masculine: ripple (Australian, natural),
 * the founder's pick after auditioning every voice on 2026-09-12; not
 * OpenAI's own default, marin, which is feminine. The web's `?voice=<name>`
 * switch (voicePreference.ts) auditions the rest.
 */
export const LIVE_DEFAULT_VOICE: LiveVoice = 'ripple';
export function isLiveVoice(v: unknown): v is LiveVoice {
  return typeof v === 'string' && (LIVE_VOICES as readonly string[]).includes(v);
}

/**
 * Voice replies on channels (spec 2026-10-03-voice-replies-on-channels-design §4.2): Bash's voice in a voice note on
 * WhatsApp, Telegram, iMessage and Slack. Ripple lives only inside gpt-live-1 sessions, so a channel voice note is
 * Gemini 3.8 Flash TTS in the closest Gemini voice, Rasalgethi (the founder's pick by ear, 2026-10-03). The style line
 * rides the request as a speech_metadata annotation; its second half keeps a Hindi reply sounding native. It sits beside
 * the Line's voice so the two are read together; the Line keeps ripple.
 */
export const CHANNEL_VOICE = {
  model: 'gemini-3.8-flash-tts',
  voice: 'Rasalgethi',
  style: 'natural, relaxed, conversational; in English, a light Australian accent; in any other language, a natural native accent',
} as const;

/** Where a line is, as the browser sees it. */
export type LiveLineState = 'starting' | 'open' | 'closing' | 'closed';

/**
 * THE LINE STAYS ON (T8b, spec 2026-09-25 §2.2). On the Mac's Dock (COMPOSER_LINE_STANDBY_V1) the
 * server ends a line for two reasons of its own, named on the state frame from `closing` on so the
 * page can act before the vendor's close lands: `standby` (quiet: no goodbye, the page's soft
 * earcon) and `rollover` (the cap: the page redials at once, seeded as every redial is).
 */
export const LINE_CLOSE_STANDBY = 'standby';
export const LINE_CLOSE_ROLLOVER = 'rollover';
/** Rollovers per conversation, at most: about 2.5 hours, then a normal goodbye. Both sides hold it. */
export const LINE_MAX_ROLLOVERS = 4;

export const LiveStatePayloadSchema = z.object({
  sessionId: z.string().min(1),
  state: z.enum(['starting', 'open', 'closing', 'closed']),
  /** OpenAI's close reason, present only on `closed`; or the standby's own (LINE_CLOSE_STANDBY, LINE_CLOSE_ROLLOVER), from `closing` on. */
  reason: z.string().optional(),
  /** Cumulative voice seconds from the last usage snapshot. */
  seconds: z.number().int().nonnegative(),
  /**
   * THE DEAF CALL (2026-09-21, ruling R-D1). Two facts only the server has, on the frame the
   * stage already listens to rather than a second channel. `awaitingVoice`: the greeting is
   * finished and the line is waiting to hear him. `heard`: the vendor produced an input
   * transcript or a delegation on this call. The browser adds what only IT can see (that he
   * spoke, and that the sent track carried it) and judges the pair in lib/voice/deaf-watch.
   * Optional: absent on every frame an older server sends, and absent is never taken for false.
   */
  awaitingVoice: z.boolean().optional(),
  heard: z.boolean().optional(),
  /**
   * ATTACH ON THE CALL (2026-09-24): how many files wait on this line for the next ask. Carried on
   * every frame of a line whose door answered `canAttach`, so a lost frame is healed by the next
   * one; 0 after a turn took them, which is when the browser clears its chips. Absent on every
   * frame with COMPOSER_LIVE_ATTACH_V1 off, and absent is never taken for 0.
   */
  staged: z.number().int().nonnegative().optional(),
});
export type LiveStatePayload = z.infer<typeof LiveStatePayloadSchema>;

/**
 * One RouterEvent of a turn the line delegated to the composer, relayed to the
 * browser so the same receipt card renders for a spoken ask as for a typed one.
 * `event` is a RouterEvent; it is `unknown` here because the RouterEvent union
 * is a TypeScript type, not a zod schema, and the browser reduces it with the
 * same `reduceEvent` the SSE lane uses.
 */
export const LiveEventPayloadSchema = z.object({
  sessionId: z.string().min(1),
  delegationId: z.string().nullable(),
  event: z.unknown(),
});
export type LiveEventPayload = z.infer<typeof LiveEventPayloadSchema>;

/**
 * The mute, browser to server: the FIRST client-to-server event of this family
 * (state and event both run the other way). A muted mic is not an absent person:
 * the vendor sends no input transcript either way, so without this the idle
 * policy reads a muted line as an empty room. Sent on every flip and once when
 * the line opens.
 *
 * NO SESSION ID. Which line this is, is the server's to know: it reads the
 * person off the authenticated socket and takes their one open line. A session
 * id in the payload would be a client-supplied key into a registry keyed by
 * session id alone, which is a stranger's call one guessed id away.
 */
/**
 * The evidence line (2026-09-21): a one-shot picture of the microphone and the uplink, sent about
 * ten seconds into a call (or at its close, if it ends sooner), so the server log can tell a dead
 * local mic from a lossy uplink from a vendor fault. It rides the mute's frame, for the mute's
 * reason: these are things about a line only the browser can see.
 *
 * PRIVACY: a closed device CLASS from the label helpers, never the label or a device id; no audio.
 * The four network numbers come from `peer.getStats()` and are null when the browser has none:
 * zero is a measurement, absent is not.
 */
export const LINE_MIC_DEVICES = ['builtin', 'bluetooth', 'phone', 'other', 'unknown'] as const;
export const LineMicStatsSchema = z.object({
  /** The largest absolute float sample seen locally so far, 0..1. Exactly 0 is a mic that delivers nothing: the browser reports any signal at all as at least 0.001, so a quiet room never reads as a dead mic. */
  micPeak: z.number(),
  /** The input on the air has produced no sample, or its source is muted or ended (the watchdog's view). */
  micDead: z.boolean(),
  /** `track.muted`: the browser says the source is delivering nothing. NOT the person's mute button. */
  trackMuted: z.boolean(),
  device: z.enum(LINE_MIC_DEVICES),
  /** How many times the watchdog put another input on the air. */
  swaps: z.number(),
  /**
   * SPEECH LEVEL, not loudness (2026-09-21, the deaf calls): `micPeak` is one sample and a door
   * slam reads 1.000, so it cannot say whether a VOICE was in the stream. `rms` is the 90th
   * percentile of the per-poll RMS over the window and `voicedMs` the total time RMS stood at or
   * above LINE_SPEECH_RMS. Optional, because a browser running an older bundle sends neither and
   * an evidence line missing them is still worth every field it does carry.
   */
  rms: z.number().optional(),
  voicedMs: z.number().optional(),
  /**
   * The browser's OWN level for the track attached to the sender (`media-source`, else
   * `outbound-rtp`), the largest seen over the window. `rms` is the analysed stream; this is the
   * one that is actually on the wire, so the two disagreeing names the swap or the sender. Null
   * where the browser reports no level, absent from an older bundle.
   */
  sentLevel: z.number().nullable().optional(),
  packetsSent: z.number().nullable(),
  bytesSent: z.number().nullable(),
  /** 0..1, as the far end reports it. */
  fractionLost: z.number().nullable(),
  rttMs: z.number().nullable(),
  /**
   * The CLOSE picture: the same fields over the whole call rather than the first window, sent as
   * the line is torn down. A call that was quiet for its first seconds and deaf afterwards reads
   * as healthy on the window line and as deaf on this one, so both are logged.
   */
  final: z.boolean().optional(),
  /**
   * The heal's outcome, on the close line of a call that climbed the ladder: which RUNG worked.
   * `mic` a different input on the same call was heard, `redial` the silent redial was heard,
   * `no` deaf after both and the stage said so. Absent on every call that never needed one,
   * which is the shape we want to stay common.
   */
  healed: z.enum(['mic', 'redial', 'no']).optional(),
  /**
   * WHAT HIS BROWSER CAPTURED (2026-09-21, after the drill). A fake mic was heard 12 of 12 on
   * staging, so the vendor and the server are fine and the difference is the capture itself.
   * `gum` how many times this page asked for a track (a page on its sixth grab is fighting the
   * browser), `track` the sent track's own settings, `ctxRate` the AudioContext's rate, and
   * `deviceNow` the class actually on the air after any swap. PRIVACY: classes and numbers, never
   * a label or an id. All optional: an older bundle sends none and its line still stands.
   */
  gum: z.number().optional(),
  track: z
    .object({
      sampleRate: z.number().optional(),
      channelCount: z.number().optional(),
      echoCancellation: z.boolean().optional(),
      noiseSuppression: z.boolean().optional(),
      autoGainControl: z.boolean().optional(),
      voiceIsolation: z.boolean().optional(),
    })
    .optional(),
  ctxRate: z.number().optional(),
  deviceNow: z.enum(LINE_MIC_DEVICES).optional(),
  /**
   * THE CUT FIRST WORD (2026-09-22). The downlink's own two facts, the mirror of the capture
   * fields above. `playback` which pipeline the remote audio actually came out of: `ctx` a source
   * node on the running AudioContext (open before the first packet arrives), `el` the `<audio>`
   * element, `fallback` a context that was still suspended when the packet landed, so the element
   * played it after all. `firstAudioMs` how long the vendor's first packet took after the answer
   * was applied, absent when no packet ever arrived. Both optional: an older bundle sends neither.
   */
  playback: z.enum(['ctx', 'el', 'fallback']).optional(),
  firstAudioMs: z.number().optional(),
});
export type LineMicStats = z.infer<typeof LineMicStatsSchema>;
/** At or above this per-poll RMS a poll holds speech, not room tone. Web measures it, both ends read it. */
export const LINE_SPEECH_RMS = 0.02;

export const LineMutePayloadSchema = z.object({
  muted: z.boolean(),
  /**
   * The silent mic (2026-09-21), on the same rail for the same reason: a microphone that delivers
   * nothing reads to the server exactly like an empty room. True once the browser's watchdog found
   * no input with signal, false when signal came back; absent on every other frame, so a healthy
   * line's frame is byte for byte what it always was.
   */
  silent: z.boolean().optional(),
  /** The evidence line (LineMicStatsSchema): the window picture, and one more at the close. */
  stats: LineMicStatsSchema.optional(),
});
export type LineMutePayload = z.infer<typeof LineMutePayloadSchema>;

/** POST /api/composer/live/session body. */
export interface LiveSessionRequest {
  /** The browser's WebRTC SDP offer. */
  sdp: string;
  host?: 'native' | 'web';
  context?: ConverseRequest['context'];
  /** A voice from LIVE_VOICES; the server falls back to the default. */
  voice?: string;
  /**
   * THE SILENT REDIAL (2026-09-21, ruling R-D1). This line replaces one the vendor never heard,
   * so it opens SILENT and listening: the opening BEGIN is skipped and nothing else about the
   * door changes. He was greeted a moment ago and is mid-thought; greeting him again would be
   * the heal talking over the very sentence it exists to rescue. Honoured only with
   * COMPOSER_LINE_DEAF_HEAL_V1 on, and only for a literal `true`.
   */
  redial?: boolean;
  /**
   * THE LINE STAYS ON (T8b): the rollovers this conversation has already spent, sent by the page
   * on every redial after its first rollover, so the new line knows its budget
   * (LINE_MAX_ROLLOVERS). A rollover opens silent, like a redial. Honoured only on the Mac's host
   * with COMPOSER_LINE_STANDBY_V1 on, and only for a whole number from 1.
   */
  rollovers?: number;
  /**
   * THE LINE STAYS ON (T8b): this page can redial a rolled-over line. Declared on EVERY native open,
   * so the server rolls over only a page that can follow; an older Dock page (a long-lived window)
   * never says it, and its cap is today's warning and goodbye. Only a literal `true` counts.
   */
  canRollover?: boolean;
}

/** POST /api/composer/live/session response (HTTP 201). */
export interface LiveSessionResponse {
  sessionId: string;
  /** The SDP answer for setRemoteDescription. */
  sdp: string;
  voice: LiveVoice;
  /**
   * The deaf-call heal is on for this line (server env COMPOSER_LINE_DEAF_HEAL_V1). The door
   * answers it rather than the browser reading its own build-time flag, so the lead can turn it
   * off on staging without waiting for a web rebuild. Absent means off.
   */
  deafHeal?: boolean;
  /**
   * Attach on the call (server env COMPOSER_LIVE_ATTACH_V1): the line takes files for the next
   * ask through POST /api/composer/live/:id/attach. Answered by the door for the same reason as
   * `deafHeal`, so the web needs no env var of its own. Absent means off.
   */
  canAttach?: boolean;
}

/** POST /api/composer/live/:id/attach body: the same wire the converse route takes. */
export interface LiveAttachRequest {
  attachments: ComposerAttachment[];
}

/** POST /api/composer/live/:id/attach response (HTTP 200): how many files now wait for the next ask. */
export interface LiveAttachResponse {
  staged: number;
}

/**
 * Attach on the call: how many files may wait on a line for one ask (the server's
 * MAX_ATTACHMENTS_PER_TURN, pinned equal by the door's test), and the door's own served sentence
 * past it. The web refuses a pick that would go past it before anything uploads, in these words.
 */
export const LIVE_ATTACH_MAX_FILES = 10;
export const LIVE_ATTACH_TOO_MANY = `Up to ${LIVE_ATTACH_MAX_FILES} files per ask`;
